import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { Warning } from "../../assets/iconComponents/Warning";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  FetalSexDownloadWarningModalWrapper,
  ActionAndCancelButtonPair,
  ModalTextButton,
} from "./DetailedResults.styles";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { getUserAuth } from "../../recoil/selectors";
import { FetalSexModalState } from "./DetailedResults.types";

interface FetalSexDownloadWarningModalProps {
  analyticsPageName:
    | "AneuploidyReportPage"
    | "CarrierReportPage"
    | "LandingPage";
  showModal: (show: FetalSexModalState) => void;
  /** Called when user confirms download (for "report" or "report_es" modes). Parent provides the appropriate action. */
  onConfirmDownload: () => Promise<void>;
}

export const FetalSexDownloadWarningModal = React.forwardRef<
  HTMLDivElement,
  FetalSexDownloadWarningModalProps
>(({ analyticsPageName, showModal, onConfirmDownload }, ref) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const [downloading, setDownloading] = useState(false);
  const auth = useRecoilValue(getUserAuth);

  useEffect(() => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth?.access_token,
        auth?.user_id,
        {
          impression_type: "modal_view",
          modal_name: "fetal_sex_report_download_confirmation",
          page_name: analyticsPageName,
        },
      );
    }
  }, [analyticsPageName, auth?.access_token, auth?.user_id]);

  const continueFunction = async () => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.interaction,
        auth?.access_token,
        auth?.user_id,
        {
          interaction_type: "button_click",
          button_text: "Continue",
          element_name: "confirm_fetal_sex_report_download",
          modal_name: "fetal_sex_report_download_confirmation",
          page_name: analyticsPageName,
        },
      );
    }
    if (!downloading) {
      setDownloading(true);
      try {
        await onConfirmDownload();
      } catch (error) {
        console.error("Failed to download report:", error);
      } finally {
        setDownloading(false);
        showModal("");
      }
    }
  };

  const getModalText = (): React.ReactNode => (
    <>
      {t("fetalSexModal.body.reportContainsSex.part1")}
      <strong>{t("fetalSexModal.body.reportContainsSex.part2")}</strong>
      {t("fetalSexModal.body.reportContainsSex.part3")}
    </>
  );

  const getModalTitle = () => t("fetalSexModal.titles.downloadReport");

  return (
    <FetalSexDownloadWarningModalWrapper
      ref={ref}
      tabIndex={-1}
      deviceType={deviceType}
    >
      <div>
        <Warning />
      </div>
      <div data-testid="reveal-sex-modal-heading">
        {GetTypographyContent({
          content: getModalTitle(),
          size: "h3",
          textColor: "redDark",
          isBold: true,
        })}
      </div>
      <div>
        {GetTypographyContent({
          content: getModalText(),
          size: "large",
          textColor: "greyMedium",
        })}
      </div>
      <ActionAndCancelButtonPair>
        <ModalTextButton
          data-testid="reveal-sex-continue-button"
          onClick={() => continueFunction()}
        >
          {t("fetalSexModal.buttons.continue")}
        </ModalTextButton>
        <ModalTextButton
          data-testid="reveal-sex-cancel-button"
          onClick={() => {
            showModal("");
            if (auth?.access_token) {
              TrackAnalyticsEvent(
                AnalyticsEventNames.interaction,
                auth?.access_token,
                auth?.user_id,
                {
                  interaction_type: "button_click",
                  button_text: "Cancel",
                  element_name: "cancel_fetal_sex_report_download",
                  modal_name: "fetal_sex_report_download_confirmation",
                  page_name: analyticsPageName,
                },
              );
            }
          }}
        >
          {t("fetalSexModal.buttons.cancel")}
        </ModalTextButton>
      </ActionAndCancelButtonPair>
    </FetalSexDownloadWarningModalWrapper>
  );
});
